/**
 * Formulario de retroalimentación post-test con escala de satisfacción
 * 
 * @author Ana Lizcano
 * @version 2.0.0
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  CardContent,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  Fade,
  Zoom
} from '@mui/material';
import {
  SentimentVerySatisfied,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
  SentimentVeryDissatisfied,
  CheckCircle,
  Send,
  Comment
} from '@mui/icons-material';
import testService from '../../../services/testService';

const FeedbackForm = ({ feedbackState, onUpdateFeedback, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const satisfactionOptions = [
        { 
      value: 'very_sad', 
      icon: SentimentVeryDissatisfied,
      label: 'Muy poco satisfecho/a',
      color: '#f44336'
    },
    
    { 
      value: 'sad', 
      icon: SentimentDissatisfied,
      label: 'Poco satisfecho/a',
      color: '#ff5722'
    },
    { 
      value: 'neutral', 
      icon: SentimentNeutral,
      label: 'Neutral',
      color: '#ff9800'
    },
    { 
      value: 'happy', 
      icon: SentimentSatisfied,
      label: 'Satisfecho/a',
      color: '#8bc34a'
    },
        { 
      value: 'very_happy', 
      icon: SentimentVerySatisfied,
      label: 'Muy satisfecho/a',
      color: '#4caf50'
    }



  ];

  const handleSubmit = async () => {
    if (!feedbackState.satisfaction) {
      setSubmitError('Por favor, selecciona una opción para continuar');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await testService.submitFeedback(
        {},
        {},
        feedbackState
      );

      if (result.success) {
        onSubmit();
      } else {
        setSubmitError('Error al enviar la retroalimentación. Inténtalo de nuevo.');
      }
    } catch (error) {
      setSubmitError('Error al enviar la retroalimentación. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white',
          textAlign: 'center',
          py: 6,
          px: 4
        }}>
          <Zoom in timeout={800}>
            <Avatar sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              mx: 'auto', 
              mb: 3, 
              width: 80, 
              height: 80,
              backdropFilter: 'blur(10px)'
            }}>
              <CheckCircle sx={{ fontSize: 40 }} />
            </Avatar>
          </Zoom>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'white !important' }}>
            ¡Test Completado!
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto', color: 'white !important' }}>
            Gracias por completar todas las preguntas. Tu retroalimentación nos ayuda a mejorar.
          </Typography>
        </Box>

        <CardContent sx={{ p: 6 }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ 
              fontWeight: 600, 
              color: '#333',
              mb: 4
            }}>
              ¿Cómo te pareció el test?
            </Typography>

            {/* Contenedor principal para las caritas alineadas */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: { xs: 1, sm: 2, md: 3 }, // Responsive spacing
              mb: 4,
              px: 2,
              // Forzar una sola fila sin wrap
              flexWrap: 'nowrap',
              // En pantallas muy pequeñas, hacer scroll horizontal si es necesario
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none'
            }}>
              {satisfactionOptions.map((option, index) => {
                const IconComponent = option.icon;
                const isSelected = feedbackState.satisfaction === option.value;
                
                return (
                  <Fade in timeout={600 + (index * 100)} key={option.value}>
                    <Box
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        // Tamaño fijo para mantener alineación
                        minWidth: { xs: 60, sm: 80, md: 100 },
                        maxWidth: { xs: 60, sm: 80, md: 100 },
                        // Flex shrink 0 para evitar que se compriman
                        flexShrink: 0,
                        '&:hover': {
                          transform: isSelected ? 'scale(1.1)' : 'scale(1.05)',
                        }
                      }}
                      onClick={() => onUpdateFeedback('satisfaction', option.value)}
                    >
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <IconComponent sx={{ 
                          fontSize: { xs: 32, sm: 40, md: 48 }, 
                          color: isSelected ? option.color : '#666',
                          transition: 'all 0.3s ease'
                        }} />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? option.color : '#333',
                            fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                            lineHeight: 1.1,
                            textAlign: 'center',
                            maxWidth: '100%',
                            wordBreak: 'break-word'
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Fade>
                );
              })}
            </Box>

          </Box>

          <Box mb={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Comment sx={{ mr: 1, color: '#666' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                Observaciones adicionales (opcional)
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={feedbackState.comments}
              onChange={(e) => onUpdateFeedback('comments', e.target.value)}
              placeholder="Comparte cualquier comentario, sugerencia o experiencia sobre el test..."
              variant="outlined"
              inputProps={{ maxLength: 500 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {feedbackState.comments.length} / 500 caracteres
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(feedbackState.comments.length / 500) * 100}
                sx={{ 
                  width: 100, 
                  height: 4, 
                  borderRadius: 2,
                  bgcolor: '#f0f0f0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: feedbackState.comments.length > 400 ? '#ff9800' : '#4caf50'
                  }
                }}
              />
            </Box>
          </Box>

          {submitError && (
            <Fade in>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {submitError}
              </Alert>
            </Fade>
          )}

          <Box textAlign="center" mb={4}>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !feedbackState.satisfaction}
              variant="contained"
              size="large"
              startIcon={isSubmitting ? null : <Send />}
              sx={{
                bgcolor: '#667eea',
                color: 'white !important',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  bgcolor: '#5a67d8',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)',
                  color: 'white !important'
                },
                '&:disabled': {
                  bgcolor: '#e31313ff',
                  color: 'white !important'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isSubmitting ? (
                <>
                  <LinearProgress sx={{ width: 20, mr: 2 }} />
                  Enviando...
                </>
              ) : (
                'Enviar Test Quadrante H'
              )}
            </Button>
          </Box>

          <Paper sx={{ 
            bgcolor: '#f8fafc', 
            p: 3, 
            borderRadius: 2,
            border: '1px solid #e2e8f0'
          }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Tu retroalimentación nos ayuda a mejorar la experiencia para futuros candidatos. 
              Todos los comentarios son revisados y considerados para optimizar nuestro proceso.
            </Typography>
          </Paper>
        </CardContent>
      </Paper>
    </Box>
  );
};

FeedbackForm.propTypes = {
  feedbackState: PropTypes.shape({
    satisfaction: PropTypes.string,
    comments: PropTypes.string
  }).isRequired,
  onUpdateFeedback: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default FeedbackForm;